import { useState, useRef } from 'react';
import store from 'src/state.js';
import {
	downloadFromS3Signed,
	searchOpenSearchNetwork,
} from 'src/_shared/services/utils.js';
import { Avatar, Badge, Popover, Select } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

let timeout;
let currentValue;

const seperateBySubCompany = (employees) => {
	const subCompanyMap = {};
	const noSubCompany = [];

	for (const employee of employees) {
		if (employee.subCompany && employee.subCompany.id) {
			const subCompanyId = employee.subCompany.id;
			subCompanyMap[subCompanyId] ||= [];
			subCompanyMap[subCompanyId].push(employee);
		} else {
			noSubCompany.push(employee);
		}
	}

	return { subCompanyMap, noSubCompany };
};

const getFullName = (employee) => {
	const firstName = employee?.firstName
		? (employee.firstName[0].toUpperCase() + employee.firstName.slice(1)).trim()
		: '';
	const lastName = employee?.lastName
		? (employee.lastName[0].toUpperCase() + employee.lastName.slice(1)).trim()
		: '';
	return `${firstName} ${lastName}`.trim();
};

const getInitials = (employee) => {
	const firstNameInitial = employee?.firstName
		? employee.firstName.charAt(0).toUpperCase()
		: '';
	const lastNameInitial = employee?.lastName
		? employee.lastName.charAt(0).toUpperCase()
		: '';
	return `${firstNameInitial}${lastNameInitial}`;
};

const getTitle = (employee) => {
	return employee?.title
		? employee.title[0].toUpperCase() + employee.title.slice(1)
		: '';
};

const renderEmployee = (employee, currentUserId) => {
	const fullName = getFullName(employee);
	const initials = getInitials(employee);
	const title = getTitle(employee);
	return (
		<>
			{employee?.avatar ? (
				<Popover content={<Avatar size={72} src={employee.avatar} />}>
					<Avatar size="small" src={employee.avatar} />
				</Popover>
			) : (
				<Avatar size="small">{initials}</Avatar>
			)}
			<span className="employee-option-text-rr">
				{fullName +
					(employee?.emailAddress ? ' - ' + employee?.emailAddress : '') +
					(employee?.title ? ' - ' + title : '')}
			</span>
		</>
	);
};

const createEmployeeOptionsArray = (
	subCompanyMap,
	noSubCompany,
	currentUserId
) => {
	const employeeOptions = [];

	if (Object.keys(subCompanyMap).length === 0) {
		if (noSubCompany?.length > 0) {
			for (const employee of noSubCompany) {
				if (employee.id !== currentUserId) {
					employeeOptions.push({
						className: 'employee-option-rr',
						label: renderEmployee(employee, currentUserId),
						value: employee.id,
					});
				}
			}
		}

		return employeeOptions;
	}

	for (const subCompanyId of Object.keys(subCompanyMap)) {
		if (subCompanyMap[subCompanyId].length > 0) {
			employeeOptions.push({
				label: (
					<span className="employee-group-label-rr">
						{subCompanyMap[subCompanyId][0].subCompany?.name || subCompanyId}
					</span>
				),
				options: subCompanyMap[subCompanyId]
					.filter((employee) => employee.id !== currentUserId)
					.map((employee) => ({
						className: 'employee-option-rr',
						label: renderEmployee(employee, currentUserId),
						value: employee.id,
					})),
			});
		}
	}

	if (noSubCompany?.length > 0) {
		employeeOptions.push({
			label: <span className="employee-group-label-rr">Other</span>,
			options: noSubCompany
				.filter((employee) => employee.id !== currentUserId)
				.map((employee) => ({
					className: 'employee-option-rr',
					label: renderEmployee(employee, currentUserId),
					value: employee.id,
				})),
		});
	}

	return employeeOptions;
};

const onSearch = async (
	value,
	callback,
	currentUser,
	setSearchedEmployeeData,
	setIsLoading
) => {
	if (timeout) {
		clearTimeout(timeout);
		timeout = null;
	}

	currentValue = value;
	const searchWithTimeout = async () => {
		const parameters = {
			fields: [
				'first_name^10',
				'last_name^10',
				'email_address^10',
				'title^2',
				'location_text^4',
			],
			query: value,
			size: 100,
			role: currentUser?.role,
			currentUser: currentUser?.id,
			filters: {
				active: 'true',
				companies: currentUser?.companyId,
				departments: [],
				fieldsToReturn: [
					'id',
					'avatar',
					'emailAddress',
					'firstName',
					'lastName',
					'subCompany',
					'title',
					'metaScore',
				],
				roles: [],
				userGroups: [],
			},
		};
		const response = await searchOpenSearchNetwork(parameters, 'erin-users');
		if (currentValue === value) {
			const searchedEmployeeResults = await Promise.all(
				response.data.map(async (record) => {
					record.avatar &&= await downloadFromS3Signed(record.avatar.key);
					return record;
				})
			);
			setSearchedEmployeeData(searchedEmployeeResults);
			const { subCompanyMap, noSubCompany } = seperateBySubCompany(
				searchedEmployeeResults
			);
			const employeeOptions = createEmployeeOptionsArray(
				subCompanyMap,
				noSubCompany,
				currentUser.id
			);
			setIsLoading(false);
			callback(employeeOptions);
		}
	};

	timeout = setTimeout(searchWithTimeout, 300);
};

const EmployeePicker = ({
	setSearchedEmployeeData = () => {},
	selectedEmployee,
	setSelectedEmployee,
	placeholder = 'Search',
	style = { width: 150 },
	dropdownStyle = { width: 'auto' },
	notFoundContent = <>Try searching by Name, Job Title, or Location</>,
	className = '',
	form = null,
	fieldName = '',
	clearOnFocus = false,
	allowMultiple = false,
	additionalRecipients = [],
	setAdditionalRecipients = () => {}
}) => {
	const [data, setData] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [selectedEmployeesData, setSelectedEmployeesData] = useState({});
	const [allFoundEmployees, setAllFoundEmployees] = useState(new Set());
	const [dropdownOpen, setDropdownOpen] = useState(false);
	const state = store.getState();
	const currentUser = state?.user?.currentUser;
	const selectRef = useRef(null);
	const [isFocused, setIsFocused] = useState(false);

	const handleSearch = async (newValue) => {
		if (!currentUser?.role || !currentUser?.id || !currentUser?.companyId)
			setData([]);
		if (newValue) {
			setIsLoading(true);
			await onSearch(
				newValue,
				(searchResults) => {
					const newFoundEmployees = new Set(allFoundEmployees);
					const allEmployees = searchResults.flatMap(item =>
						item.options ? item.options : [item]
					);
					
					const updatedEmployeesData = { ...selectedEmployeesData };
					allEmployees.forEach(employee => {
						if (!newFoundEmployees.has(employee.value)) {
							newFoundEmployees.add(employee.value);
							
							const labelChildren = employee.label?.props?.children;
							const nameSpan = labelChildren?.[1]?.props?.children;
							const fullName = typeof nameSpan === 'string' ? nameSpan.split(' - ')[0] : 'Unknown';
							
							const nameParts = fullName.split(' ');
							const firstName = nameParts[0] || '';
							const lastName = nameParts[1] || '';
							const initials = `${firstName?.charAt(0)?.toUpperCase()}${lastName?.charAt(0)?.toUpperCase()}`;
							
							const combinedSelected = [selectedEmployee, ...additionalRecipients].filter(Boolean);
							if (combinedSelected.includes(employee.value)) {
								updatedEmployeesData[employee.value] = {
									fullName: fullName,
									initials: initials
								};
							}
						}
					});
					
					setAllFoundEmployees(newFoundEmployees);
					setSelectedEmployeesData(updatedEmployeesData);
					setData(searchResults);
				},
				currentUser,
				setSearchedEmployeeData,
				setIsLoading
			);
		} else {
			setData([]);
		}
	};

	const handleChange = (newValue) => {
		if (allowMultiple) {
			if (Array.isArray(newValue)) {
				const allEmployees = data.flatMap(item =>
					item.options ? item.options : [item]
				);
				
				const newEmployeesData = { ...selectedEmployeesData };
				newValue.forEach(employeeId => {
					if (!newEmployeesData[employeeId]) {
						const employee = allEmployees.find(emp => emp.value === employeeId);
						if (employee) {
							const labelChildren = employee.label?.props?.children;
							const nameSpan = labelChildren?.[1]?.props?.children;
							const fullName = typeof nameSpan === 'string' ? nameSpan.split(' - ')[0] : 'Unknown';
							
							const nameParts = fullName.split(' ');
							const firstName = nameParts[0] || '';
							const lastName = nameParts[1] || '';
							const initials = `${firstName?.charAt(0)?.toUpperCase()}${lastName?.charAt(0)?.toUpperCase()}`;
							
							newEmployeesData[employeeId] = {
								fullName: fullName,
								initials: initials
							};
						}
					}
				});
				
				const filteredEmployeesData = {};
				newValue.forEach(employeeId => {
					if (newEmployeesData[employeeId]) {
						filteredEmployeesData[employeeId] = newEmployeesData[employeeId];
					}
				});
				
				setSelectedEmployeesData(filteredEmployeesData);

				if (newValue.length === 0) {
					setSelectedEmployee(null);
					setAdditionalRecipients([]);
				} else if (newValue.length === 1) {
					setSelectedEmployee(newValue[0]);
					setAdditionalRecipients([]);
				} else {
					setSelectedEmployee(newValue[0]);
					setAdditionalRecipients(newValue.slice(1));
				}
			}
		} else {
			setSelectedEmployee(newValue);
			setDropdownOpen(false);
		}

		if (form) {
			form.setFieldsValue({ [fieldName]: newValue });
		}
	};

	const combinedValue = allowMultiple
		? [selectedEmployee, ...additionalRecipients].filter(Boolean)
		: selectedEmployee;

	const customTagRender = (props) => {
		const { label, value, closable, onClose } = props;
		const employeeData = selectedEmployeesData[value];
		const initials = employeeData?.initials || '??';
		
		return (
			<span
				style={{
					display: 'inline-flex',
					alignItems: 'center',
					padding: '2px 8px',
					margin: '0px 2px',
					backgroundColor: 'var(--primary-btn-color, var(--theme-primary))',
					border: `1px solid var(--primary-btn-color, var(--theme-primary))`,
					borderRadius: '12px',
					fontSize: '12px',
					fontWeight: '500',
					color: '#fff',
					marginBottom: '2px'
				}}
			>
				{initials}
				{closable && (
					<span
						style={{
							marginLeft: '4px',
							cursor: 'pointer',
							fontSize: '10px',
							color: '#fff'
						}}
						onClick={onClose}
					>
						×
					</span>
				)}
			</span>
		);
	};

	const totalSelected = combinedValue ? (Array.isArray(combinedValue) ? combinedValue.length : 1) : 0;
	const overflowCount = totalSelected > 3 ? totalSelected - 3 : 0;

	return (
		<div style={{ position: 'relative', display: 'inline-block' }}>
			<Select
				ref={selectRef}
				showSearch
				mode={allowMultiple ? "multiple" : undefined}
				className={className + " select-recipient-search"}
				value={combinedValue}
				placeholder={isFocused ? '' : placeholder}
				style={{ ...style }}
				loading={isLoading}
				suffixIcon={
					<LoadingOutlined style={isLoading ? {} : { display: 'none' }} />
				}
				defaultActiveFirstOption={false}
				dropdownMatchSelectWidth={false}
				dropdownStyle={dropdownStyle}
				filterOption={false}
				notFoundContent={notFoundContent}
				spellCheck={false}
				options={data}
				onSearch={handleSearch}
				onChange={handleChange}
				{...(allowMultiple ? {
					tagRender: customTagRender
				} : {
					onSelect: () => {
						selectRef.current.blur();
					}
				})}
				onFocus={() => clearOnFocus ? setSelectedEmployee(null) : setIsFocused(true) }
				onBlur={() => setIsFocused(false)}
			/>
			{allowMultiple && overflowCount > 0 && (
				<Badge
					count={`+${overflowCount}`}
					style={{
						position: 'absolute',
						top: '-24px',
						right: '-6px',
						backgroundColor: 'var(--primary-btn-color, var(--theme-primary))',
						color: '#fff',
						fontSize: '11px',
						height: '19px',
						minWidth: '19px',
						lineHeight: '19px',
						padding: '0',
						borderRadius: '20px',
						border: 'none',
						boxShadow: '0 0 0 1px #fff'
					}}
				/>
			)}
		</div>
	);
};

export default EmployeePicker;
